/**
 * Versions section for managing version selections per table
 *
 * Uses unified versionSelections: Record<alias, string> for all tables
 * - Primary table (tables[0]) and all relationship tables use the same interface
 * - No special "base" concept - all tables treated equally
 */

import { useState, useEffect, useMemo } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import type { LinkedTableSourceApi } from '@loader/shared-types';
import { VersionCombobox, type VersionOption } from '@loader/ui';

import { isRelationshipValid } from '@/features/linkedTables/editLinkedTable/hooks/useLinkedTableEditor';
import { useRpc } from '@/lib/rpc';
import type { LinkedTableRelationship } from '@/lib/types';

interface VersionsSectionProps {
  table_id: string;
  /** All tables in the linked table (tables[0] is the primary table) */
  tables: LinkedTableSourceApi[];
  relationships: LinkedTableRelationship[];
  /** Unified version selections keyed by table alias */
  versionSelections: Record<string, string>;
  /** Unified handler for updating any table's version selection */
  onUpdateVersionSelection: (alias: string, selection: string) => void;
}

/**
 * Section for managing version selections for all tables
 */
export function VersionsSection({
  table_id,
  tables,
  relationships,
  versionSelections,
  onUpdateVersionSelection,
}: VersionsSectionProps) {
  const rpc = useRpc();
  // Versions for the primary table (tables[0])
  const [primaryTableVersions, setPrimaryTableVersions] = useState<VersionOption[]>([]);
  const [relationshipTables, setRelationshipTables] = useState<
    Record<string, { id: string; name: string; versions: VersionOption[] }>
  >({});
  const [loading, setLoading] = useState(true);

  // Get primary table info
  const primaryTable = tables[0];
  const primaryTableAlias = primaryTable?.alias || '';
  const primaryTableName = primaryTable?.alias || 'Primary Table';

  /**
   * Fetch primary table versions on component mount or when table_id changes
   * Only includes completed versions
   */
  useEffect(() => {
    const fetchPrimaryTableVersions = async () => {
      try {
        const res = await rpc.api.private.versions.$get({
          query: { table_id },
        });

        if (!res.ok) throw new Error('Failed to fetch versions');
        const response = await res.json();

        // API returns { data: versions[], pagination: {...} }
        const versionsArray = response.data || [];

        // Filter for completed versions only
        const versions = (versionsArray as VersionOption[])
          .filter((v) => v.status === 'completed');

        setPrimaryTableVersions(versions);
      } catch (error) {
        console.error('Failed to fetch primary table versions:', error);
      }
    };

    if (table_id) {
      void fetchPrimaryTableVersions();
    }
  }, [table_id, rpc]);

  /**
   * Filter out invalid relationships to prevent API errors
   * Only fetch data for relationships with target_id, target_type, and alias
   */
  const validRelationships = useMemo(() => {
    return relationships.filter(isRelationshipValid);
  }, [relationships]);

  /**
   * Fetch relationship tables and their versions
   */
  useEffect(() => {
    const fetchRelationshipData = async () => {
      try {
        setLoading(true);
        const tablesMap: Record<
          string,
          { id: string; name: string; versions: VersionOption[] }
        > = {};

        // Fetch table info and versions for each valid relationship
        // Skip linkedTable relationships - linkedTables handle their own version resolution at query time
        await Promise.all(
          validRelationships
            .filter((rel) => rel.target_type === 'table')
            .map(async (rel) => {
              const tableId = rel.target_id;
              let tableName = rel.target_name || rel.alias;

              // Fetch table details to get the name
              try {
                // @ts-expect-error - Hono RPC type inference limitation
                const tableRes = await rpc.api.private.tables[':id'].$get({
                  param: { id: tableId },
                });
                if (tableRes.ok) {
                  const tableResponse = await tableRes.json();
                  tableName = tableResponse.table.name;
                }
              } catch (error) {
                console.error(`Failed to fetch table ${tableId}:`, error);
              }

              // Fetch versions for this table
              try {
                const versionsRes = await rpc.api.private.versions.$get({
                  query: { table_id: tableId },
                });
                if (versionsRes.ok) {
                  const versionsResponse = await versionsRes.json();
                  const versionsArray = versionsResponse.data || [];

                  // Filter for completed versions only
                  const versions = (versionsArray as VersionOption[])
                    .filter((v) => v.status === 'completed');

                  tablesMap[rel.alias] = {
                    id: tableId,
                    name: tableName,
                    versions,
                  };
                }
              } catch (error) {
                console.error(`Failed to fetch versions for ${tableId}:`, error);
                tablesMap[rel.alias] = {
                  id: tableId,
                  name: tableName,
                  versions: [],
                };
              }
            })
        );

        setRelationshipTables(tablesMap);
      } catch (error) {
        console.error('Failed to fetch relationship data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (validRelationships.length > 0) {
      void fetchRelationshipData();
    } else {
      setLoading(false);
    }
  }, [validRelationships, rpc]);

  // Get current version selection for primary table
  const primaryVersionSelection = versionSelections[primaryTableAlias] || 'current';

  return (
    <div className="space-y-3">
      {/* Preview-only warning */}
      <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Version selections are for preview only. The version will be requested at query time when data is pulled.</span>
      </div>

      {/* Primary Table Version Selection */}
      <div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-24 shrink-0 truncate text-sm">
            {primaryTableName}
          </span>
          <VersionCombobox
            versions={primaryTableVersions}
            value={primaryVersionSelection}
            onChange={(value) => onUpdateVersionSelection(primaryTableAlias, value)}
            showSystemTagShortcuts
            showDate
            className="bg-background min-w-0 flex-1"
          />
        </div>
      </div>

      {/* Relationship Version Selections */}
      {relationships.length > 0 && (
        <div className="space-y-3">
          {loading ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading...
            </div>
          ) : (
            relationships.map((rel) => {
              // Only show version selection for table relationships
              // LinkedTables use their own internal version configuration
              if (rel.target_type === 'linked_table') {
                return (
                  <div key={rel.id} className="flex items-center gap-2">
                    <span className="text-muted-foreground w-24 shrink-0 truncate text-sm">
                      {rel.alias}
                    </span>
                    <span className="text-muted-foreground flex-1 text-xs italic">
                      Using linkedTable's configured version
                    </span>
                  </div>
                );
              }

              const tableInfo = relationshipTables[rel.alias];
              const versionOptions = tableInfo?.versions || [];
              const currentSelection = versionSelections[rel.alias] || 'current';

              return (
                <div key={rel.id} className="flex items-center gap-2">
                  <span className="text-muted-foreground w-24 shrink-0 truncate text-sm">
                    {tableInfo?.name || rel.alias}
                  </span>
                  <VersionCombobox
                    versions={versionOptions}
                    value={currentSelection}
                    onChange={(value) => onUpdateVersionSelection(rel.alias, value)}
                    showSystemTagShortcuts
                    showDate
                    className="bg-background min-w-0 flex-1"
                  />
                </div>
              );
            })
          )}
        </div>
      )}

    </div>
  );
}
